import { useEffect, type ReactNode } from 'react'
import { ApolloProvider } from '@apollo/client/react'
import { Toaster } from 'sonner'
import { apolloClient } from '@/lib/apollo'
import { adoptPlatformSession } from '@/lib/auth/sessionAdoption'
import { AppErrorBoundary } from '@/shared/components/ui/app-error-boundary'
import { TooltipProvider } from '@/shared/components/ui/tooltip'
import '@/lib/i18n'

/**
 * Providers globales del portal.
 *
 * Orden de outside-in:
 * 1. `AppErrorBoundary` (Sentry + UI estilizada) — capa más externa para que
 *    cualquier error de los providers internos termine en el fallback elegante,
 *    no en una pantalla blanca.
 * 2. `ApolloProvider` — habilita `useQuery` / `useMutation` en todo el árbol.
 * 3. `TooltipProvider` — Radix Tooltip necesita un provider global.
 * 4. `Toaster` (sonner) — notificaciones globales, montado una sola vez.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  // Adopción de la sesión de plataforma al arrancar (§5.6 del CLAUDE.md
  // raíz): si ya hay sesión SSO viva (cookie httpOnly del gateway), el token
  // se hidrata en silencio y el usuario no vuelve a pasar por /login. No
  // bloquea el render; sin sesión se degrada a visitante.
  useEffect(() => {
    void adoptPlatformSession()
  }, [])

  return (
    <AppErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <TooltipProvider delayDuration={200}>
          {children}
          <Toaster position="top-right" richColors />
        </TooltipProvider>
      </ApolloProvider>
    </AppErrorBoundary>
  )
}
